import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type Language } from "@nh/shared";

/**
 * La elección EXPLÍCITA de idioma (Anahí, 14-sep-2026): «con la
 * posibilidad de cambiar el idioma, pero no los precios».
 *
 * Va en su propia llave y no en la caché del detector de i18next
 * (`user_language_preference`), porque esa la escribe CUALQUIER
 * `changeLanguage` — también los automáticos por país— y entonces no
 * se distinguiría "lo eligió" de "se lo pusimos". Solo el selector
 * escribe aquí; `useCountryLanguage` la respeta por encima del país.
 */
const CHOSEN_KEY = "language_chosen";

function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

export function readChosenLanguage(): Language | null {
  try {
    const stored = window.localStorage.getItem(CHOSEN_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function useLanguage(): [Language, (language: Language) => void] {
  const { i18n } = useTranslation();
  const current: Language = isLanguage(i18n.language) ? i18n.language : "es";

  const setLanguage = useCallback(
    (language: Language) => {
      try {
        window.localStorage.setItem(CHOSEN_KEY, language);
      } catch {
        // No poder recordarlo no impide cambiarlo en esta visita.
      }
      void i18n.changeLanguage(language);
    },
    [i18n],
  );

  return [current, setLanguage];
}
